import styled from 'styled-components';

//----------------------------------------------------------------------
// SECTION LAYOUT
//----------------------------------------------------------------------

export const Base = styled.div`
	display: flex;
	flex-direction: column;
	gap: 32px;
	width: 100%;
`;

export const Subsection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
`;

export const SubsectionTitle = styled.h3`
	font-size: 14px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[700]};
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin: 0;
	padding-bottom: 8px;
	border-bottom: 1px solid ${({ theme }) => theme.gray[200]};
`;

//----------------------------------------------------------------------
// PREVIEW BAR
//----------------------------------------------------------------------

export const PreviewBar = styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 8px 12px;
	border-radius: 8px;
	background-color: ${({ theme }) => theme.gray[100]};
	border: 1px solid ${({ theme }) => theme.gray[200]};
`;

export const PreviewInput = styled.input`
	flex: 1;
	border: none;
	background: transparent;
	font-size: 14px;
	color: ${({ theme }) => theme.gray[900]};
	outline: none;
	font-family: inherit;

	&::placeholder {
		color: ${({ theme }) => theme.gray[400]};
	}
`;

export const BgToggle = styled.button<{ $active?: boolean }>`
	width: 24px;
	height: 24px;
	border-radius: 6px;
	border: 2px solid ${({ theme, $active }) => ($active ? theme.blue[500] : theme.gray[400])};
	flex-shrink: 0;
	transition: border-color 0.15s ease;

	&:hover {
		border-color: ${({ theme }) => theme.blue[500]};
	}
`;

export const BgToggleLight = styled(BgToggle)`
	background-color: ${({ theme }) => theme.gray[0]};
`;

export const BgToggleDark = styled(BgToggle)`
	background-color: ${({ theme }) => theme.gray[900]};
`;

//----------------------------------------------------------------------
// FONT CARDS (read-only showcase)
//----------------------------------------------------------------------

export const FontCardList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`;

export const FontCard = styled.div`
	border: 1px solid ${({ theme }) => theme.gray[200]};
	border-radius: 10px;
	overflow: hidden;
	background-color: ${({ theme }) => theme.gray[0]};
`;

export const FontCardHeader = styled.div<{ $dark?: boolean }>`
	padding: 24px;
	background-color: ${({ theme, $dark }) => ($dark ? theme.gray[900] : theme.gray[0])};
	transition: background-color 0.2s ease;
`;

export const FontCardName = styled.div`
	font-size: 13px;
	font-weight: 600;
	color: ${({ theme }) => theme.gray[600]};
	text-transform: uppercase;
	letter-spacing: 0.04em;
	margin-bottom: 12px;
	display: flex;
	align-items: center;
	gap: 8px;
`;

export const SourceBadge = styled.span`
	font-size: 10px;
	font-weight: 500;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	padding: 2px 6px;
	border-radius: 4px;
	background-color: ${({ theme }) => theme.gray[100]};
	color: ${({ theme }) => theme.gray[600]};
`;

export const FontCardPreview = styled.div<{ $dark?: boolean }>`
	font-size: 32px;
	line-height: 1.3;
	color: ${({ theme, $dark }) => ($dark ? theme.gray[0] : theme.gray[1000])};
	transition: color 0.2s ease;
	word-break: break-word;
`;

export const FontCardCharset = styled.div<{ $dark?: boolean }>`
	font-size: 16px;
	line-height: 1.6;
	color: ${({ theme, $dark }) => ($dark ? theme.gray[400] : theme.gray[600])};
	margin-top: 16px;
	letter-spacing: 0.02em;
	transition: color 0.2s ease;
`;

export const FontCardMeta = styled.div`
	padding: 12px 24px;
	border-top: 1px solid ${({ theme }) => theme.gray[200]};
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
`;

export const WeightPill = styled.button<{ $active?: boolean }>`
	font-size: 12px;
	padding: 4px 10px;
	border-radius: 12px;
	border: 1px solid ${({ theme, $active }) => ($active ? theme.blue[500] : theme.gray[200])};
	background-color: ${({ theme, $active }) => ($active ? theme.blue[300] : 'transparent')};
	color: ${({ theme, $active }) => ($active ? theme.blue[800] : theme.gray[700])};
	font-weight: 500;
	transition: all 0.15s ease;

	&:hover {
		border-color: ${({ theme }) => theme.blue[500]};
	}
`;

//----------------------------------------------------------------------
// RECIPE SPECIMENS (compact, stacked type specimen sheet)
//----------------------------------------------------------------------

export const SpecimenList = styled.div<{ $dark?: boolean }>`
	display: flex;
	flex-direction: column;
	gap: 0;
	background-color: ${({ theme, $dark }) => ($dark ? theme.gray[900] : 'transparent')};
	border-radius: 8px;
	transition: background-color 0.2s ease;
`;

export const SpecimenRow = styled.button<{ $selected?: boolean; $dark?: boolean }>`
	display: flex;
	align-items: baseline;
	gap: 16px;
	padding: 6px 8px;
	border: none;
	border-radius: 4px;
	background-color: ${({ theme, $selected, $dark }) =>
		$selected ? theme.blue[300] : $dark ? theme.gray[900] : 'transparent'};
	color: ${({ theme, $dark }) => ($dark ? theme.gray[0] : theme.gray[1000])};
	text-align: left;
	font: inherit;
	width: 100%;
	transition: all 0.15s ease;
	position: relative;

	&:hover {
		background-color: ${({ theme, $selected, $dark }) =>
			$selected ? theme.blue[300] : $dark ? theme.gray[800] : theme.gray[100]};
	}
`;

export const SpecimenLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[600]};
	width: 120px;
	flex-shrink: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const SpecimenText = styled.div`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const SpecimenTooltip = styled.div`
	position: absolute;
	bottom: 100%;
	left: 16px;
	transform: translateY(-4px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 8px 12px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: pre-line;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;
	max-width: 400px;
	line-height: 1.5;

	${SpecimenRow}:hover & {
		opacity: 1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 24px;
		border: 6px solid transparent;
		border-top-color: ${({ theme }) => theme.gray[900]};
	}
`;

//----------------------------------------------------------------------
// TYPE SCALE
//----------------------------------------------------------------------

export const TypeScaleList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;
	width: 100%;
`;

export const TypeScaleRow = styled.button<{ $selected?: boolean; $dark?: boolean }>`
	display: flex;
	align-items: baseline;
	gap: 16px;
	padding: 8px 8px;
	border: none;
	border-radius: 4px;
	background-color: ${({ theme, $selected, $dark }) =>
		$selected ? theme.blue[300] : $dark ? theme.gray[900] : 'transparent'};
	text-align: left;
	font: inherit;
	width: 100%;
	position: relative;
	transition: background-color 0.15s ease;

	&:hover {
		background-color: ${({ theme, $selected, $dark }) =>
			$selected ? theme.blue[300] : $dark ? theme.gray[800] : theme.gray[100]};
	}
`;

export const TypeScaleLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[600]};
	width: 120px;
	flex-shrink: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

export const TypeScaleSample = styled.div<{ $dark?: boolean }>`
	flex: 1;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	color: ${({ theme, $dark }) => ($dark ? theme.gray[0] : theme.gray[1000])};
	transition: color 0.2s ease;
`;

export const TypeScaleValue = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	width: 60px;
	text-align: right;
	flex-shrink: 0;
`;

export const TypeScaleTooltip = styled.div`
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(-4px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: nowrap;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;

	${TypeScaleRow}:hover & {
		opacity: 1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 6px solid transparent;
		border-top-color: ${({ theme }) => theme.gray[900]};
	}
`;

//----------------------------------------------------------------------
// WEIGHT SCALE
//----------------------------------------------------------------------

export const WeightGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 24px;
	align-items: flex-start;
`;

export const WeightItem = styled.button<{ $selected?: boolean; $dark?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 12px 16px;
	border-radius: 8px;
	border: 1px solid ${({ theme, $selected }) => ($selected ? theme.blue[500] : 'transparent')};
	background-color: ${({ theme, $dark }) => ($dark ? theme.gray[900] : 'transparent')};
	transition: all 0.15s ease;
	font: inherit;
	position: relative;
	min-width: 80px;

	&:hover {
		background-color: ${({ theme, $dark }) => ($dark ? theme.gray[800] : theme.gray[100])};
	}
`;

export const WeightSample = styled.div<{ $dark?: boolean }>`
	font-size: 22px;
	line-height: 1.2;
	color: ${({ theme, $dark }) => ($dark ? theme.gray[0] : theme.gray[1000])};
	text-align: center;
	white-space: nowrap;
	transition: color 0.2s ease;
`;

export const WeightLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[900]};
	text-align: center;
`;

export const WeightValue = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	text-align: center;
`;

export const WeightTooltip = styled.div`
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(-4px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: nowrap;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;

	${WeightItem}:hover & {
		opacity: 1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 6px solid transparent;
		border-top-color: ${({ theme }) => theme.gray[900]};
	}
`;

//----------------------------------------------------------------------
// LINE HEIGHT SCALE
//----------------------------------------------------------------------

export const LineHeightGrid = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 24px;
	align-items: flex-start;
`;

export const LineHeightItem = styled.button<{ $selected?: boolean; $dark?: boolean }>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 8px;
	border-radius: 8px;
	border: 1px solid ${({ theme, $selected }) => ($selected ? theme.blue[500] : 'transparent')};
	background-color: ${({ theme, $dark }) => ($dark ? theme.gray[900] : 'transparent')};
	transition: all 0.15s ease;
	font: inherit;
	position: relative;
	width: 240px;

	&:hover {
		background-color: ${({ theme, $dark }) => ($dark ? theme.gray[800] : theme.gray[100])};
	}
`;

export const LineHeightSample = styled.div<{ $dark?: boolean }>`
	font-size: 13px;
	width: 100%;
	text-align: left;
	color: ${({ theme, $dark }) => ($dark ? theme.gray[0] : theme.gray[1000])};
	border-left: 2px solid ${({ theme }) => theme.blue[500]};
	padding-left: 8px;
	transition: color 0.2s ease;
`;

export const LineHeightLabel = styled.div`
	font-size: 12px;
	font-weight: 500;
	color: ${({ theme }) => theme.gray[900]};
	text-align: center;
`;

export const LineHeightValue = styled.div`
	font-size: 11px;
	color: ${({ theme }) => theme.gray[600]};
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	text-align: center;
`;

export const LineHeightTooltip = styled.div`
	position: absolute;
	bottom: 100%;
	left: 50%;
	transform: translateX(-50%) translateY(-4px);
	background-color: ${({ theme }) => theme.gray[900]};
	color: ${({ theme }) => theme.gray[0]};
	padding: 6px 10px;
	border-radius: 6px;
	font-size: 12px;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
		'Courier New', monospace;
	white-space: nowrap;
	pointer-events: none;
	opacity: 0;
	transition: opacity 0.2s ease;
	z-index: 10;

	${LineHeightItem}:hover & {
		opacity: 1;
	}

	&::after {
		content: '';
		position: absolute;
		top: 100%;
		left: 50%;
		transform: translateX(-50%);
		border: 6px solid transparent;
		border-top-color: ${({ theme }) => theme.gray[900]};
	}
`;
